import {
  ICreateDraftJobPostApiResponse,
  ICreateDraftJobResult,
  IDraftJobPostEntity,
  IGetDraftJobPostApiResponse,
  IGetDraftJobPostResult,
  IPublishDraftJobPostPayload,
  IWorkingInterviewApiPayload,
} from '../types';
import { ProfessionTypeEnum } from '~/models/Profession';
import { PayType, ScheduleType, JobPostingStatus } from '~/domain/PermanentJobs/enums';
import { IWorkingInterview } from '~/reducers/permanentJobsReducer/States';
import moment from 'moment';

const toCreateDraftJobPostResult = (
  response: ICreateDraftJobPostApiResponse,
): ICreateDraftJobResult => {
  return {
    id: response.data.postingId,
  } satisfies ICreateDraftJobResult;
};

const toGetDraftJobPostResult = (response: IGetDraftJobPostApiResponse): IGetDraftJobPostResult => {
  return {
    data: {
      id: response.data.id,
      officeId: response.data.officeId,
      title: response.data.title,
      professionType: response.data.professionType as ProfessionTypeEnum,
      subProfession: response.data.subProfession,
      officeSpecialty: response.data.officeSpecialty,
      payType: response.data.payType as PayType,
      minRate: response.data.minRate,
      maxRate: response.data.maxRate,
      yearlyRate: response.data.yearlyRate,
      benefits: response.data.benefits,
      benefitsOther: response.data.benefitsOther,
      scheduleType: response.data.scheduleType as ScheduleType,
      expectedDays: response.data.expectedDays,
      description: response.data.description,
      status: response.data.status as JobPostingStatus,
      createdAt: response.data.createdAt,
      updatedAt: response.data.updatedAt,
      publishedAt: response.data.publishedAt,
      closedAt: response.data.closedAt,
      createdBy: response.data.createdBy,
      updatedBy: response.data.updatedBy,
      closedBy: response.data.closedBy,
    } satisfies IDraftJobPostEntity,
  } satisfies IGetDraftJobPostResult;
};

/**
 * Helper function to parse break time string to minutes
 * Handles formats: "30 min", "1 hr", "1.5 hr", "2 hr", etc.
 */
const parseBreakTime = (breakTime: string | null): number | null => {
  if (!breakTime) return null;

  // Extract the number from the string (handles decimals)
  const match = breakTime.match(/(\d+\.?\d*)/);
  if (!match) return null;

  const value = parseFloat(match[1]);
  if (isNaN(value)) return null;

  // Check if it's in hours format
  if (breakTime.toLowerCase().includes('hr')) {
    // Convert hours to minutes
    return Math.round(value * 60);
  }

  // Otherwise it's in minutes
  return Math.round(value);
};

const toPublishDraftJobPostPayload = (
  workingInterviews: IWorkingInterview[],
): IPublishDraftJobPostPayload => {
  return {
    workingInterviews: workingInterviews.map(
      (interview) =>
        ({
          breakTime: parseBreakTime(interview.breakTime),
          localDate: moment(interview.date).format('YYYY-MM-DD'),
          rate: parseFloat(interview.hourlyRate),
          localStart: moment(interview.hoursArrival, 'hh:mm a').format('HH:mm'),
          localEnd: moment(interview.hoursDeparture, 'hh:mm a').format('HH:mm'),
          isAssistedHygiene: interview.isAssistedHygiene,
          isBreakPaid: interview.isPaidBreakTime,
          specialty: interview.specialty,
          // TODO: Handle overlapping working interviews, Temporarily set to always true
          duplicate: true,
        }) satisfies IWorkingInterviewApiPayload,
    ),
  } satisfies IPublishDraftJobPostPayload;
};

export const DraftJobPostAdapters = {
  toCreateDraftJobPostResult,
  toGetDraftJobPostResult,
  toPublishDraftJobPostPayload,
};
